import type { Day, Navigation, NavigationSegment, Place, Stop } from "../types";

export interface SegmentMetric extends NavigationSegment {
  estimated: boolean;
}

export interface StopTiming {
  arrivalMinutes: number;
  departureMinutes: number;
  dwellMinutes: number;
  estimated: boolean;
}

export function buildDaySchedule(day: Day, navigation?: Navigation, nodes: Record<string, Place> = {}): StopTiming[] {
  if (!day.stops.length) return [];
  const segments = daySegmentMetrics(day, navigation, nodes);
  let cursor = parseClock(day.departureTime || day.stops[0].arrival || "09:00") ?? 9 * 60;
  return day.stops.map((stop, index) => {
    if (index > 0) cursor += Math.ceil((segments[index - 1]?.durationSeconds ?? 0) / 60);
    // Once a structured daily departure exists, every arrival is derived.
    // Legacy per-stop arrival strings remain a fallback for imported routes.
    const explicitArrival = day.departureTime ? null : parseClock(stop.arrival);
    const arrivalMinutes = explicitArrival === null ? cursor : alignClockAfter(explicitArrival, cursor);
    const dwellMinutes = stopDwellMinutes(stop);
    const departureMinutes = arrivalMinutes + dwellMinutes;
    cursor = departureMinutes;
    return { arrivalMinutes, departureMinutes, dwellMinutes, estimated: explicitArrival === null };
  });
}

export function daySegmentMetrics(day: Day, navigation?: Navigation, nodes: Record<string, Place> = {}): SegmentMetric[] {
  const routedSegments = navigation?.segments;
  const segmentsMatchStops = routedSegments?.length === Math.max(0, day.stops.length - 1) && routedSegments.every((segment, index) => segment.fromNode === day.stops[index]?.node && segment.toNode === day.stops[index + 1]?.node);
  if (navigation?.status === "complete" && segmentsMatchStops && routedSegments) {
    return routedSegments.map((segment) => ({ ...segment, estimated: false }));
  }
  const pairs = day.stops.slice(1).map((stop, index) => ({
    fromNode: day.stops[index].node,
    toNode: stop.node,
    weight: directDistance(nodes[day.stops[index].node], nodes[stop.node])
  }));
  const totalWeight = pairs.reduce((sum, pair) => sum + pair.weight, 0);
  return pairs.map((pair) => ({
    fromNode: pair.fromNode,
    toNode: pair.toNode,
    distanceMeters: navigation?.distanceMeters && totalWeight ? navigation.distanceMeters * pair.weight / totalWeight : 0,
    durationSeconds: navigation?.durationSeconds && totalWeight ? navigation.durationSeconds * pair.weight / totalWeight : 0,
    estimated: true
  }));
}

export function stopDwellMinutes(stop: Stop) {
  if (Number.isFinite(stop.dwellMinutes)) return Math.max(0, Math.round(stop.dwellMinutes!));
  const text = stop.duration ?? "";
  if (/出发|到达|入住/.test(text)) return 0;
  const hours = text.match(/([\d.]+)\s*(?:小时|h)/i);
  const minutes = text.match(/(\d+)\s*(?:分钟|min)/i);
  if (!hours && !minutes) return 0;
  return Math.round(Number(hours?.[1] ?? 0) * 60 + Number(minutes?.[1] ?? 0));
}

export function formatClock(totalMinutes: number) {
  const dayOffset = Math.floor(totalMinutes / 1440);
  const normalized = ((totalMinutes % 1440) + 1440) % 1440;
  const time = `${String(Math.floor(normalized / 60)).padStart(2, "0")}:${String(normalized % 60).padStart(2, "0")}`;
  return dayOffset > 0 ? `${time} +${dayOffset}天` : time;
}

export function formatTravelDuration(seconds = 0) {
  const minutes = Math.max(0, Math.round(seconds / 60));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours ? `${hours} 小时${rest ? ` ${rest} 分` : ""}` : `${rest} 分钟`;
}

function parseClock(value?: string) {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) return null;
  const [hours, minutes] = value.split(":").map(Number);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function alignClockAfter(clock: number, cursor: number) {
  while (clock + 12 * 60 < cursor) clock += 1440;
  return clock;
}

function directDistance(a?: Place, b?: Place) {
  if (!a || !b) return 1;
  const rad = (value: number) => value * Math.PI / 180;
  const dLat = rad(b.coord[0] - a.coord[0]);
  const dLng = rad(b.coord[1] - a.coord[1]);
  const value = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.coord[0])) * Math.cos(rad(b.coord[0])) * Math.sin(dLng / 2) ** 2;
  return Math.max(1, 6371000 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value)));
}
